import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import * as userStore from '@cartwhl/common/stores/userStore';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

type VerificationState = 'loading' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerificationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const email = searchParams.get('email');
  const code = searchParams.get('code');
  const excitedAbout = searchParams.get('excitedAbout') ?? undefined;

  useEffect(() => {
    async function verifyEmail() {
      if (!email || !code) {
        setState('error');
        setErrorMessage('Missing email or verification code.');
        return;
      }

      const result = await userStore.verifyWaitlistEmail(
        email,
        code,
        excitedAbout,
      );

      if (result.success) {
        setState('success');
      } else {
        setState('error');
        setErrorMessage(
          result.error || 'Verification failed. Please try again.',
        );
      }
    }

    verifyEmail();
  }, [email, code, excitedAbout]);

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="container flex min-h-[60vh] flex-col items-center justify-center py-20">
      {state === 'loading' && (
        <div className="flex flex-col items-center gap-4">
          <Text variant="web/display/xl">Verifying your email...</Text>
          <Text variant="web/body/medium/md" className="text-secondary">
            Please wait while we confirm your email address.
          </Text>
        </div>
      )}

      {state === 'success' && (
        <div className="flex flex-col items-center gap-6 text-center">
          <Text variant="web/display/xl">Email verified!</Text>
          <Text variant="web/body/medium/md" className="text-secondary">
            Thank you for confirming your email. We'll notify you when there's
            news to share.
          </Text>
          <Button variant="primary" onClick={handleGoHome}>
            Go to Homepage
          </Button>
        </div>
      )}

      {state === 'error' && (
        <div className="flex flex-col items-center gap-6 text-center">
          <Text variant="web/display/xl">Verification failed</Text>
          <Text variant="web/body/medium/md" className="text-secondary">
            {errorMessage}
          </Text>
          <Button variant="primary" onClick={handleGoHome}>
            Go to Homepage
          </Button>
        </div>
      )}
    </div>
  );
}
